import { useEffect } from 'react';
import { trackView, type TrackableEntity } from '@/services/tracking';
import type { DetailStatus } from '@/utils/detailStatus';

/**
 * Count a view for an entity detail page.
 *
 * Usage (in a detail page) — always the entity's own id, never the route key,
 * which may be a slug (web plan W0.16):
 *   const { item: brand, status } = useEntityPage(useBrandDetail);
 *   useTrackView('brand', brand?.id, status);
 *
 * Nothing is counted until the page's detail `status` is 'ok', i.e. the
 * entity was actually shown. A well-formed id that doesn't exist (or is
 * hidden: blocked, draft, another country) is `not_found` and counts no
 * view; a failed load counts none either (web plan W0.19).
 *
 * The underlying service dedupes per entity per session (so a single mount
 * counts once), silently ignores invalid ids and sends nothing for crawlers or
 * automated browsers (a Googlebot render of a detail page counts no view — see
 * `isLikelyBot`), so pages can call this unconditionally. The view joins the
 * same queue as card impressions and goes out with the next `trackViews` batch
 * (within ~1.5s, or when the page is hidden/closed), not as a request of its
 * own.
 */
export function useTrackView(
  entity: TrackableEntity,
  id: string | undefined | null,
  status: DetailStatus,
): void {
  const shown = status === 'ok';
  useEffect(() => {
    if (id && shown) trackView(entity, id);
  }, [entity, id, shown]);
}
